/**
 * State management hook for Assistant Runtime
 * Centralizes all useState calls and state operations
 */

import type { ThreadMessage } from '@/types/messages';
import { useCallback, useState } from 'react';
import { initialAssistantState } from './AssistantState';

export function useAssistantState() {
  const [messages, setMessages] = useState<ThreadMessage[]>(
    initialAssistantState.messages,
  );
  const [error, setError] = useState<string | null>(
    initialAssistantState.error,
  );
  const [currentThreadId, setCurrentThreadId] = useState<string | null>(
    initialAssistantState.currentThreadId,
  );
  const [availableIds, setAvailableIds] = useState<Set<string>>(
    initialAssistantState.availableIds,
  );

  // Per-thread streaming state
  const [streamingThreadIds, setStreamingThreadIds] = useState<Set<string>>(
    new Set(),
  );

  const setThreadStreaming = useCallback(
    (threadId: string, streaming: boolean) => {
      setStreamingThreadIds((prev) => {
        const next = new Set(prev);
        if (streaming) {
          next.add(threadId);
        } else {
          next.delete(threadId);
        }
        return next;
      });
    },
    [],
  );

  // Derived value: true when the current thread is actively streaming.
  const isLoading = currentThreadId
    ? streamingThreadIds.has(currentThreadId)
    : false;

  const addAvailableIds = useCallback((ids: string[]) => {
    setAvailableIds((prev) => {
      const newSet = new Set(prev);
      ids.forEach((id) => newSet.add(id));
      return newSet;
    });
  }, []);

  return {
    // State
    messages,
    isLoading,
    error,
    currentThreadId,
    availableIds,
    streamingThreadIds,

    // Actions
    setMessages,
    setError,
    setCurrentThreadId,
    setAvailableIds,
    addAvailableIds,
    setThreadStreaming,
  };
}
